import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaTrash, FaShoppingCart, FaCheck } from 'react-icons/fa';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { getUserCountry, detectUserLocation } from '../utils/geolocation';
import { useAuth } from '../context/AuthContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import ProFeatureLocked from '../components/ProFeatureLocked';
import { getShoppingListPageSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { useTranslation } from 'react-i18next';
import { hasPremiumAccess } from '../config/subscriptions';

// BuyButton component to display dynamic supplier
const BuyButton = ({ mappingId, redirectApi, fetchSupplier, userCountry }) => {
  const { t } = useTranslation();
  const [supplierInfo, setSupplierInfo] = useState(null);

  useEffect(() => {
    fetchSupplier(mappingId).then(info => {
      setSupplierInfo(info);
    });
  }, [mappingId, fetchSupplier]);

  if (!supplierInfo) return null;

  return (
    <a
      href={`${redirectApi}/${mappingId}?country=${userCountry}`}
      target="_blank"
      rel="noopener noreferrer"
      data-tour="buy-button"
      className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium ml-10 hover:underline transition-colors"
    >
      <FaShoppingCart className="w-4 h-4" />
      <span>{t('shoppingList.shopAt', 'Indkøb hos {{supplier}}', { supplier: supplierInfo.displayName })}</span>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
};

const ShoppingListPage = ({ sessionId }) => {
  const { user, updateCompletedTours } = useAuth();
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierCache, setSupplierCache] = useState({});
  const [allMappings, setAllMappings] = useState([]);
  const [userCountry, setUserCountry] = useState('DK');
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const tourInitialized = useRef(false);

  // Use direct API endpoints (no proxy needed)
  const REDIRECT_API = `${API}/go`;
  const ADMIN_REDIRECT_API = `${API}/admin`;
  const ADMIN_TOKEN = 'dev-token-change-in-production';
  
  const getMappingId = (ingredientName) => {
    const name = ingredientName.toLowerCase().trim();
    const ingredientWords = name.split(/\s+/); // Split ingredient name into words
    
    let bestMatch = null;
    let bestMatchScore = 0;
    
    // Check against all mappings' keywords
    for (const mapping of allMappings) {
      if (mapping.keywords) {
        const allKeywords = mapping.keywords.toLowerCase().split(/[,;]/).map(k => k.trim()).filter(k => k);
        
        // First check: Exact match (highest priority)
        if (allKeywords.includes(name)) {
          return mapping.id; // Exact match - return immediately
        }
        
        // Second check: Match only if ingredient has SAME number of words as keyword
        // AND each word matches exactly (not as substring)
        for (const keyword of allKeywords) {
          const keywordWords = keyword.split(/\s+/);
          
          // Only match if word count is the same
          if (keywordWords.length !== ingredientWords.length) {
            continue;
          }
          
          // All ingredient words must match exactly (as complete words, not substrings)
          // This prevents "vand" from matching "vandmelon"
          const allWordsMatch = ingredientWords.every(ingredientWord => 
            keywordWords.some(keywordWord => keywordWord === ingredientWord)
          );
          
          if (allWordsMatch) {
            // Calculate match score based on keyword length (more specific = better)
            const score = keyword.length;
            
            if (score > bestMatchScore) {
              bestMatch = mapping.id;
              bestMatchScore = score;
            }
          }
        }
      }
    }
    
    return bestMatch;
  };

  useEffect(() => {
    fetchMappingsAndShoppingList();
    // Detect user country for product links via backend API (IP-based)
    detectUserLocation().then(result => {
      setUserCountry(result.country_code);
      console.log('[ShoppingList] User country detected:', result.country_code, 'Source:', result.source);
    }).catch(error => {
      console.error('[ShoppingList] Country detection failed:', error);
      setUserCountry('DK'); // Fallback to Denmark
    });
  }, [sessionId]);


  // Start tour for first-time users
  useEffect(() => {
    if (hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.SHOPPING_LIST, user) && !tourInitialized.current && items.length > 0) {
      console.log('[Tour] Starting shopping list tour...');
      tourInitialized.current = true;
      setTimeout(() => {
        setCurrentTourStep(0);
      }, 1500);
    }
  }, [user, items]);

  const handleTourNext = () => {
    setCurrentTourStep(prev => prev + 1);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.SHOPPING_LIST, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.SHOPPING_LIST, API, updateCompletedTours);
    setCurrentTourStep(-1);
    toast.success(t('tour.shopping.tourComplete', 'Indkøbsliste guide færdig!'));
  };


  const fetchSupplierInfo = async (mappingId) => {
    // Check cache first
    if (supplierCache[mappingId]) {
      console.log(`Using cached supplier for ${mappingId}:`, supplierCache[mappingId]);
      return supplierCache[mappingId];
    }

    try {
      console.log(`Fetching supplier info for mapping: ${mappingId}`);
      const response = await axios.get(`${ADMIN_REDIRECT_API}/mapping/${mappingId}`, {
        headers: { Authorization: `Bearer ${ADMIN_TOKEN}` }
      });
      
      console.log(`Response for ${mappingId}:`, response.data);
      
      if (response.data && response.data.options && response.data.options.length > 0) {
        const activeOption = response.data.options.find(opt => opt.status === 'active') || response.data.options[0];
        console.log(`Active option for ${mappingId}:`, activeOption);
        
        const supplierInfo = {
          supplier: activeOption.supplier,
          displayName: getSupplierDisplayName(activeOption.supplier)
        };
        
        console.log(`Supplier info for ${mappingId}:`, supplierInfo);
        
        // Cache it
        setSupplierCache(prev => ({ ...prev, [mappingId]: supplierInfo }));
        return supplierInfo;
      }
    } catch (error) {
      console.error(`Error fetching supplier info for ${mappingId}:`, error);
    }
    
    // Return null instead of default fallback
    console.warn(`No supplier found for ${mappingId}, returning null`);
    return null;
  };

  const getSupplierDisplayName = (supplier) => {
    const names = {
      'power': 'Power',
      'barshopen': 'Barshopen',
      'bilka': 'Bilka',
      'foetex': 'Føtex',
      'matas': 'Matas',
      'nemlig': 'Nemlig.com',
      'amazon': 'Amazon',
      'other': 'Leverandør'
    };
    return names[supplier] || supplier.charAt(0).toUpperCase() + supplier.slice(1);
  };

  const fetchMappingsAndShoppingList = async () => {
    try {
      // Clear supplier cache when fetching new mappings
      setSupplierCache({});
      
      // Fetch all mappings with keywords (non-blocking - continue even if this fails)
      try {
        const mappingsResponse = await axios.get(`${ADMIN_REDIRECT_API}/mappings`, {
          headers: { Authorization: `Bearer ${ADMIN_TOKEN}` }
        });
        setAllMappings(mappingsResponse.data);
        console.log('[ShoppingList] Mappings loaded:', mappingsResponse.data.length);
      } catch (mappingsError) {
        console.warn('[ShoppingList] Mappings API failed (non-critical):', mappingsError.message);
        // Continue anyway - shopping list will work without buy buttons
        setAllMappings([]);
      }
      
      // Fetch shopping list (this is the critical part)
      await fetchShoppingList();
    } catch (error) {
      console.error('Error fetching shopping list:', error);
      setLoading(false);
    }
  };

  const fetchShoppingList = async () => {
    try {
      console.log('[ShoppingList] Fetching with sessionId:', sessionId);
      const response = await axios.get(`${API}/shopping-list/${sessionId}`);
      console.log('[ShoppingList] Received items:', response.data.length);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching shopping list:', error);
      toast.error(t('shoppingList.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const toggleCheck = async (itemId, currentChecked) => {
    try {
      await axios.put(`${API}/shopping-list/${itemId}`, null, {
        params: { checked: !currentChecked }
      });
      setItems(items.map(item =>
        item.id === itemId ? { ...item, checked: !currentChecked } : item
      ));
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error('Kunne ikke opdatere');
    }
  };

  const deleteItem = async (itemId) => {
    try {
      await axios.delete(`${API}/shopping-list/${itemId}`);
      setItems(items.filter(item => item.id !== itemId));
      toast.success('Fjernet fra liste');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Kunne ikke fjerne');
    }
  };

  const clearCompleted = async () => {
    const completedItems = items.filter(item => item.checked);
    try {
      for (const item of completedItems) {
        await axios.delete(`${API}/shopping-list/${item.id}`);
      }
      setItems(items.filter(item => !item.checked));
      toast.success('Fjernet afkrydsede');
    } catch (error) {
      console.error('Error clearing completed:', error);
      toast.error('Kunne ikke rydde liste');
    }
  };

  const groupedItems = items.reduce((acc, item) => {
    const recipeName = item.linked_recipe_name || 'Generelt';
    if (!acc[recipeName]) acc[recipeName] = [];
    acc[recipeName].push(item);
    return acc;
  }, {});

  const uncheckedCount = items.filter(item => !item.checked).length;

  // Guest gating — show elegant Pro-locked card instead of the empty list.
  // Access gate: shopping list requires a login (any registered user).
  // Kun anonyme gæster blokeres — Free-brugere har fuld adgang.
  if (!user) {
    return (
      <ProFeatureLocked
        title={t('shoppingList.title')}
        description={t('shoppingList.guestSubtitle', 'Saml alle ingredienser fra dine favorit-opskrifter på én indkøbsliste.')}
        bullets={[
          t('shoppingList.bullet1', 'Tilføj ingredienser fra opskrifter med ét klik'),
          t('shoppingList.bullet2', 'Bliver automatisk lagt sammen på tværs af opskrifter'),
          t('shoppingList.bullet3', 'Direkte “Køb hos…”-links til danske leverandører'),
        ]}
        testId="shopping-list-locked"
      />
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="shopping-list-page">
      {/* Onboarding Tour */}
      <OnboardingTooltip
        steps={getShoppingListPageSteps()}
        currentStep={currentTourStep}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
        onFinish={handleTourFinish}
      />
      
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl font-bold mb-2">{t('shoppingList.title')}</h1>
          <p className="text-gray-600">
            {t('shoppingList.remainingItems', `{{count}} ingrediens{{plural}} tilbage`, { 
              count: uncheckedCount, 
              plural: uncheckedCount !== 1 ? 'er' : '' 
            })}
          </p>
        </div>
        {items.some(item => item.checked) && (
          <Button
            onClick={clearCompleted}
            data-testid="clear-completed-button"
            variant="outline"
          >
            {t('shoppingList.clearCompleted')}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><FaShoppingCart /></div>
          <h3 className="text-xl font-bold mb-2">{t('shoppingList.emptyTitle', 'Din indkøbsliste er tom')}</h3>
          <p className="text-gray-600">{t('shoppingList.emptyDescription', 'Tilføj manglende ingredienser fra opskrifter')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([recipeName, recipeItems]) => (
            <div key={recipeName} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
              <h3 className="font-bold text-lg mb-4 text-cyan-600">{recipeName}</h3>
              <div className="space-y-2">
                {recipeItems.map((item) => {
                  const mappingId = getMappingId(item.ingredient_name);
                  return (
                    <div
                      key={item.id}
                      data-testid={`shopping-item-${item.id}`}
                      data-tour="shopping-item"
                      className={`p-3 rounded-lg transition-all ${
                        item.checked ? 'bg-gray-50 opacity-60' : 'bg-cyan-50'
                      }`}
                    >
                      <div className="flex items-center gap-4 mb-2">
                        <button
                          onClick={() => toggleCheck(item.id, item.checked)}
                          data-testid={`check-${item.id}`}
                          className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                            item.checked
                              ? 'bg-green-500 border-green-500'
                              : 'border-gray-300 hover:border-cyan-500'
                          }`}
                        >
                          {item.checked && <FaCheck className="text-white" size={12} />}
                        </button>
                        <div className="flex-1">
                          <p className={`font-medium ${
                            item.checked ? 'line-through text-gray-500' : 'text-gray-800'
                          }`}>
                            {item.ingredient_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {item.quantity} {item.unit}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteItem(item.id)}
                          data-testid={`delete-${item.id}`}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaTrash />
                        </button>
                      </div>
                      {mappingId && !item.checked && (
                        <BuyButton 
                          mappingId={mappingId}
                          redirectApi={REDIRECT_API}
                          fetchSupplier={fetchSupplierInfo}
                          userCountry={userCountry}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ShoppingListPage;